import { Component, Fragment } from 'react';
import { FormControl, FormGroup as FormGroupBase } from 'react-bootstrap';
import { $Formutil, connect } from 'react-bootstrap-formutil';
import FormGroup from 'components/FormGroup';
import UsStatesSelect from 'components/UsStatesSelect';
import config from 'utils/config';
import { printf } from 'utils/i18n';

/**
 * @param isComboName: 判断是否是一个由 “.” 组合的 name 属性
 */
interface BaseAddressProps {
    hasPostalCode?: boolean;
    isProvince?: boolean;
    prefix: 'delivery' | 'regulatory_company_other.company' | 'home';
    disabled?: boolean;
    $formutil: $Formutil<any>;
    isComboName?: boolean;
    prefixStreetPlaceholder?: string;
}

class BaseAddress extends Component<BaseAddressProps> {
    static defaultProps = {
        isComboName: false,
        isProvince: true,
        hasPostalCode: true
    };

    parser = (value, $setViewValue) => $setViewValue(value.trim());

    validMessage = {
        states: {
            maxLength: __('Exceeded maximum length'),
            isCity: printf(
                __('The %s you entered is not valid'),
                this.props.isProvince ? __('state/province') : __('state')
            )
        },
        address: {
            required: config.inputAError(__('address'), true),
            isAddress: __('The address you entered is not valid')
        },
        city: {
            required: config.inputAError(__('city'), false),
            isCity: __('The city you entered is not valid'),
            maxLength: __('Exceeded maximum length')
        },
        zip: {
            required: config.inputError(__('zip code')),
            isZip: __('Zip code should be 5 digit')
        },
        postal: {
            required: config.inputError(__('zip/postal code')),
            isPostal: __('The postal code you entered is not valid')
        }
    };

    $validators = {
        isAddress: (value, prop, { validMessage: { isAddress } }) => {
            if (this.props.disabled) {
                return true;
            }

            // 不使用\s，避免非半角空格通过校验
            if (/^[a-zA-Z\d \-/:;()$&@.,?![\]{}#%^*+=_\\|~<>]*$/g.test(value)) {
                return true;
            }

            return isAddress;
        },
        isZip: (value, prop, { validMessage: { isZip } }) => {
            if (this.props.disabled) {
                return true;
            }

            if (/^\d{5}$/g.test(value)) {
                return true;
            }

            return isZip;
        },
        isPostal: (value, prop, { validMessage: { isPostal } }) => {
            if (this.props.disabled) {
                return true;
            }

            //  不使用\s，避免非半角空格通过校验
            if (/^[a-zA-Z\d -]{1,10}$/g.test(value)) {
                return true;
            }

            return isPostal;
        },
        isCity: (value, prop, { validMessage: { isCity } }) => {
            if (this.props.disabled) {
                return true;
            }

            // 不使用\s，避免非半角空格通过校验
            if (/^[a-zA-Z -]+$/g.test(value)) {
                return true;
            }

            return isCity;
        },
        isMatch: () => true
    };

    render() {
        const {
            prefix,
            isProvince,
            hasPostalCode,
            $formutil: { $params },
            isComboName,
            prefixStreetPlaceholder,
            disabled
        } = this.props;

        let prefixCountry = $params[`${prefix}_country`];

        if (isComboName && $params[prefix.split('.')[0]]) {
            prefixCountry = $params[prefix.split('.')[0]][`${prefix.split('.')[1]}_country`];
        }

        return (
            <Fragment>
                <FormGroup
                    label={__('Street Address')}
                    name={`${prefix}_address`}
                    required={!disabled}
                    isAddress
                    $validators={this.$validators}
                    validMessage={this.validMessage.address}>
                    <FormControl
                        placeholder={printf(__('%s Address'), String(prefixStreetPlaceholder))}
                        disabled={disabled}
                    />
                </FormGroup>
                <FormGroup
                    label={__('Apt/Ste/Bldg')}
                    name={`${prefix}_address2`}
                    isAddress
                    $validators={this.$validators}
                    validMessage={this.validMessage.address}>
                    <FormControl
                        placeholder={printf(__('%s Address'), String(prefixStreetPlaceholder))}
                        disabled={disabled}
                    />
                </FormGroup>
                <FormGroupBase>
                    <FormGroup
                        label={__('City')}
                        name={`${prefix}_city`}
                        required={!disabled}
                        isCity
                        maxLength={prefixCountry && prefixCountry !== 'USA' ? 30 : null}
                        $validators={this.$validators}
                        validMessage={this.validMessage.city}>
                        <FormControl placeholder={__('City')} disabled={disabled} />
                    </FormGroup>
                    <FormGroup
                        label={isProvince ? __('State/Province') : __('State')}
                        name={`${prefix}_state_code`}
                        isMatch
                        isCity
                        maxLength={prefixCountry && prefixCountry !== 'USA' ? 30 : null}
                        $validators={this.$validators}
                        validMessage={this.validMessage.states}
                        checker={value => {
                            if (disabled) {
                                return true;
                            }

                            if (!value) {
                                return prefixCountry && prefixCountry !== 'USA'
                                    ? config.inputAError(
                                          this.props.isProvince ? __('state/province') : __('state'),
                                          false
                                      )
                                    : config.selectAddressError(
                                          this.props.isProvince ? __('state/province') : __('state'),
                                          false
                                      );
                            }

                            return true;
                        }}
                        $onFieldChange={() => {
                            this.props.$formutil.$getField(`${prefix}_postal`)?.$validate();
                        }}>
                        {isProvince && prefixCountry && prefixCountry !== 'USA' ? (
                            <FormControl
                                placeholder={`${isProvince ? __('State/Province') : __('State')}`}
                                disabled={disabled}
                            />
                        ) : (
                            <UsStatesSelect disabled={disabled} prefix={prefix} />
                        )}
                    </FormGroup>
                </FormGroupBase>
                {hasPostalCode && prefixCountry && prefixCountry !== 'USA' ? (
                    <FormGroup
                        key="postal"
                        label={__('Zip/Postal Code')}
                        name={`${prefix}_postal`}
                        required={!disabled}
                        isPostal
                        isMatch
                        $validators={this.$validators}
                        validMessage={this.validMessage.postal}
                        $onFieldChange={() => {
                            this.props.$formutil.$getField(`${prefix}_state_code`)?.$validate();
                        }}>
                        <FormControl placeholder={__('Zip/Postal Code')} disabled={disabled} />
                    </FormGroup>
                ) : (
                    <FormGroup
                        key="zip"
                        label={__('Zip Code')}
                        name={`${prefix}_postal`}
                        required={!disabled}
                        isZip
                        isMatch
                        $parser={this.parser}
                        $validators={this.$validators}
                        validMessage={this.validMessage.zip}
                        $onFieldChange={() => {
                            this.props.$formutil.$getField(`${prefix}_state_code`)?.$validate();
                        }}>
                        <FormControl placeholder={__('Zip Code')} disabled={disabled} />
                    </FormGroup>
                )}
            </Fragment>
        );
    }
}

export default connect(BaseAddress);
